import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { motion } from "framer-motion";
import { Target, Newspaper } from "lucide-react";
import { FaTrophy } from "react-icons/fa6";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { FaUser } from "react-icons/fa6";
const tabs = [
  { id: "top", label: "Top Picks", enable: "true" },
  { id: "signal", label: "Signal", enable: "true" },
  { id: "market", label: "Market", enable: "true" },
  { id: "news", label: "News", enable: "true" },
  { id: "profile", label: "Profile", enable: "true" },
];

export default function Footer() {
  const locate = useLocation();
  const containerRef = useRef(null);
  const buttonsRef = useRef({});
  const [indicator, setIndicator] = useState({ left: 0, width: 48 });
  const navigate = useNavigate();

  const updateIndicator = (tabId, { animate = true } = {}) => {
    const btn = buttonsRef.current[tabId];
    const container = containerRef.current;
    if (!btn || !container) return;
    const btnRect = btn.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const desiredWidth = Math.min(64, Math.round(btnRect.width * 0.64));
    const center = btnRect.left - containerRect.left + btnRect.width / 2;
    const left = Math.round(center - desiredWidth / 2);

    setIndicator({ left, width: desiredWidth });
  };
  const pathToTab = {
    "/": "market",
    "/signal": "signal",
    "/topSignals": "top",
    // "/login": "",
    "/news": "news",
    "/newsDetail/:id": "news",
    "/profile": "profile",
    "/invite": "profile",
    "/financial-report": "profile",
    "/support": "profile",
    "/ban-history": "profile",
    "/completeProfile": "profile",
    "/privacy-policy": "profile",
    "/top-traders": "top",
  };
  const active =
    pathToTab[locate.pathname] ||
    (/^\/newsDetail\//.test(locate.pathname) ? "news" : "market");

  useLayoutEffect(() => {
    updateIndicator(active);
  }, [locate.pathname]);

  function onItemClick(it) {
    if (it === "market") navigate("/");
    else if (it === "signal") navigate("/signal");
    else if (it === "top") navigate("/topSignals");
    else if (it === "news") navigate("/news");
    else if (it === "profile") navigate("/profile");
  }

  const baseItemClasses =
    "relative flex flex-col items-center py-3 px-4 rounded-xl transition-transform duration-200";

  return (
    <div className="w-screen left-1/2 -translate-x-1/2 fixed bottom-0">
      <style>{`
        :root{ --accent-from: #2563eb; --accent-to: #1e40af; --inactive: #9CA3AF; --indicator-h: 6px; }
        .footer-wrap { backdrop-filter: blur(12px); background: rgba(255,255,255,0.95); border-top: 1px solid #E5E7EB; }
        

        /* micro animations */
        @keyframes signal-flash {0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.2);opacity:.75}}
        @keyframes trophy-glow {0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.2);opacity:.55}}
        @keyframes news-pulse {0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.00);opacity:.75}}
        @keyframes profile-float {0%,100%{transform:translateY(0)}50%{transform:translateY(-2px)}}

        .signal-flash{animation:signal-flash 2.6s infinite ease-in-out}
        .trophy-glow{animation:trophy-glow 2.6s infinite ease-in-out}
        .news-pulse{animation:news-pulse 2.2s infinite ease-in-out}
        .profile-float{animation:profile-float 3s infinite ease-in-out}

        /* indicator */
        .indicator { position:absolute; bottom:1px; height:var(--indicator-h); background: linear-gradient(90deg,var(--accent-from),var(--accent-to)); border-radius:9999px; box-shadow: 0 6px 18px rgba(30,64,175,0.12); }
      `}</style>

      <div className="footer-wrap h-[90px]">
        <div
          ref={containerRef}
          className="relative px-6 max-w-3xl mx-auto flex justify-between items-center h-full"
        >
          {tabs.map((tab) => (
            <button
              disabled={!tab.enable}
              key={tab.id}
              ref={(el) => (buttonsRef.current[tab.id] = el)}
              onClick={() => onItemClick(tab.id)}
              className={`${baseItemClasses} ${active === tab.id ? "nav-activ bg-gradient-to-br from-[#0000FF] to-[#4169E1] text-white" : "text-gray-500"}  w-[60px] h-[60px]`}
              aria-pressed={active === tab.id}
            >
              <div
                className={` ${tab.id === "signal" ? "signal-flash bg-primary-light bg-opacity-40 rounded-full transform " : ""} ${tab.id === "top" ? "trophy-glow" : ""} ${tab.id === "news" ? "news-pulse" : ""} ${tab.id === "profile" ? "profile-float" : ""}`}
              >
                {active === "top" && tab.id == "top" ? (
                  <FaTrophy size={22}></FaTrophy>
                ) : tab.id == "top" ? (
                  <FaTrophy size={18}></FaTrophy>
                ) : null}
                {active === "signal" && tab.id == "signal" ? (
                  <Target size={22}></Target>
                ) : tab.id == "signal" ? (
                  <Target size={18}></Target>
                ) : null}
                {active === "market" && tab.id == "market" ? (
                  <div className="nav-icon text-2xl flex -mt-2 items-end justify-center w-8 h-8 space-x-0.5 relative">
                    {[
                      { height: "60%", delay: "-0.4s", bg: "bg-white" },
                      { height: "90%", delay: "-0.2s", bg: "bg-white" },
                      { height: "75%", delay: "0s", bg: "bg-white" },
                      { height: "60%", delay: "0.2s", bg: "bg-white" },
                    ].map((bar, idx) => (
                      <div
                        key={idx}
                        className={`w-[5px] animate-candle  ${bar.bg}`}
                        style={{
                          height: bar.height,
                          animationDelay: bar.delay,
                        }}
                      />
                    ))}
                  </div>
                ) : tab.id == "market" ? (
                  <div className="nav-icon text-2xl flex  items-end justify-center w-6 h-6 space-x-0.5 relative">
                    {[
                      { height: "70%", delay: "-0.4s", bg: "bg-gray-500" },
                      { height: "100%", delay: "-0.2s", bg: "bg-gray-500" },
                      { height: "85%", delay: "0s", bg: "bg-gray-500" },
                      { height: "70%", delay: "0.2s", bg: "bg-gray-500" },
                    ].map((bar, idx) => (
                      <div
                        key={idx}
                        className={`w-[4px] animate-candle  ${bar.bg}`}
                        style={{
                          height: bar.height,
                          animationDelay: bar.delay,
                        }}
                      />
                    ))}
                  </div>
                ) : null}
                {active === "news" && tab.id == "news" ? (
                  <>
                    <div className="w-2 rounded-full h-2 bg-red-500 animate-pulse  absolute -top-1 -z-20 right-0"></div>
                    <Newspaper size={22}></Newspaper>
                  </>
                ) : tab.id == "news" ? (
                  <>
                    <div className="w-2 rounded-full h-2 bg-red-500 animate-pulse  absolute -top-1 -z-20 right-0"></div>
                    <Newspaper size={18}></Newspaper>
                  </>
                ) : null}
                {active === "profile" && tab.id == "profile" ? (
                  <FaUser size={22}></FaUser>
                ) : tab.id == "profile" ? (
                  <FaUser size={18}></FaUser>
                ) : null}
              </div>
              <span
                className={` whitespace-nowrap ${active === tab.id ? "text-[12px]" : "text-[10px]"}`}
              >
                {tab.label}
              </span>
            </button>
          ))}

          {/* Indicator: use framer-motion to animate left & width with spring */}
          <motion.div
            className="indicator bg-gradient-to-br from-[#0000FF] to-[#4169E1]"
            animate={{ left: indicator.left, width: indicator.width }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            style={{ position: "absolute" }}
          />
        </div>
      </div>
    </div>
  );
}
